import * as XLSX from "xlsx";
import {
  extractPassengers,
  extractRouteList,
  extractSchoolFromKeyValue,
  looksLikeKeyValue,
  looksLikePersonName,
  rosterHeaderSignals,
} from "./import/heuristic-extract";

export const SPREADSHEET_EXTENSIONS = [".xlsx", ".xls", ".csv"];
export const MAX_SPREADSHEET_BYTES = 8 * 1024 * 1024;

const FIELD_ALIASES = {
  name: ["name", "student", "student name", "passenger", "passenger name", "full name", "öğrenci", "ad soyad", "ad", "isim"],
  firstName: ["first name", "firstname", "given name", "ad"],
  lastName: ["last name", "lastname", "surname", "family name", "soyad"],
  school: ["school", "campus", "site", "location name", "okul", "kurum"],
  address: ["address", "street", "home address", "pickup address", "dropoff address", "adres", "sokak"],
  parent: ["parent", "parent name", "guardian", "contact name", "veli", "veli adı"],
  phone: ["phone", "mobile", "parent phone", "guardian phone", "telefon", "cep"],
  email: ["email", "e-mail", "parent email", "e-posta"],
  grade: ["grade", "class", "year", "sınıf"],
};

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ");

export const validateSpreadsheetFile = (file) => {
  if (!file) {
    return { ok: false, error: "Choose a spreadsheet to continue." };
  }
  if (typeof file.size === "number" && file.size <= 0) {
    return { ok: false, error: "That file is empty. Upload a spreadsheet with at least one student row." };
  }
  if (typeof file.size === "number" && file.size > MAX_SPREADSHEET_BYTES) {
    return { ok: false, error: "That file is larger than 8 MB. Upload a smaller spreadsheet." };
  }
  const name = String(file.name || "").toLowerCase();
  const allowed = SPREADSHEET_EXTENSIONS.some((ext) => name.endsWith(ext));
  if (!allowed) {
    return { ok: false, error: "Use an Excel or CSV file (.xlsx, .xls, or .csv)." };
  }
  return { ok: true };
};

export const guessColumnMapping = (headers = []) => {
  const mapping = {
    name: null,
    firstName: null,
    lastName: null,
    school: null,
    address: null,
    parent: null,
    phone: null,
    email: null,
    grade: null,
  };

  headers.forEach((header, index) => {
    const normalized = normalizeHeader(header);
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
      if (mapping[field] != null) return;
      if (aliases.includes(normalized)) mapping[field] = index;
    });
  });

  return mapping;
};

const cellString = (value) => {
  if (value == null) return "";
  return String(value).replace(/\s+/g, " ").trim();
};

const rowHasValues = (row = []) => row.some((cell) => cellString(cell) !== "");

const flattenHeader = (value, index) => cellString(value) || `Column ${index + 1}`;

const looksLikeRouteSheet = (headers = []) => {
  const blob = headers.join(" ").toLocaleLowerCase("tr");
  return /güzergah|guzergah|plaka|sürücü|surucu|route name|vehicle plate/.test(blob);
};

const detectKind = (headers, rows) => {
  if (looksLikeKeyValue(rows)) return "keyValue";
  const signals = rosterHeaderSignals(headers);
  const { passengers } = extractPassengers(headers, rows);
  const named = passengers.filter((passenger) => passenger.name).length;
  if (named >= 3 && signals.name && signals.any) return "roster";
  if (looksLikeRouteSheet(headers) && !signals.parent && !signals.address) return "routes";
  if (named >= 3) return "roster";
  const nameHits = rows.slice(0, 20).filter((row) => looksLikePersonName(row[0])).length;
  if (nameHits >= 5) return "roster";
  if (named >= 1 && rows.length <= 2) return "roster";
  return "other";
};

const parseSheetMatrix = (sheet) => {
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, blankrows: false, defval: "" });
  return matrix.filter(rowHasValues);
};

const parseOneSheet = (sheet, sheetName) => {
  const rows = parseSheetMatrix(sheet);
  if (!rows.length) {
    return { sheetName, kind: "empty", headers: [], rows: [] };
  }
  if (looksLikeKeyValue(rows)) {
    return {
      sheetName,
      kind: "keyValue",
      headers: ["Field", "Value"],
      rows: rows.map((row) => [cellString(row[0]), cellString(row[1])]),
      school: extractSchoolFromKeyValue(rows),
    };
  }
  const first = rows[0].map((cell, index) => flattenHeader(cell, index));
  const firstLooksNumeric = first.every((cell) => cell === "" || cell.startsWith("Column ") || !Number.isNaN(Number(cell)));
  const headerInferred = firstLooksNumeric && first.some((cell) => cell && !cell.startsWith("Column "));
  const headerRow = headerInferred ? first.map((_, index) => `Column ${index + 1}`) : rows[0].map((cell, index) => flattenHeader(cell, index));
  const dataRows = headerInferred ? rows : rows.slice(1);
  const headers = headerRow.map((cell, index) => cell || `Column ${index + 1}`);
  const normalizedRows = dataRows.map((row) => headers.map((_, index) => cellString(row[index]))).filter(rowHasValues);
  const kind = detectKind(headers, normalizedRows);
  const parsedSheet = {
    sheetName,
    kind,
    headers,
    rows: normalizedRows,
    headerInferred,
  };
  if (kind === "routes") parsedSheet.routes = extractRouteList(headers, normalizedRows).routes;
  return parsedSheet;
};

export const workbookToTable = (workbook) => {
  const sheetNames = workbook.SheetNames || [];
  if (!sheetNames.length) {
    throw new Error("This workbook has no sheets.");
  }

  const sheets = sheetNames.map((name) => parseOneSheet(workbook.Sheets[name], name)).filter((sheet) => sheet.kind !== "empty");
  if (!sheets.length) {
    throw new Error("We could not find any rows in that spreadsheet.");
  }

  const rosterSheets = sheets.filter((sheet) => sheet.kind === "roster");
  const keyValueSheets = sheets.filter((sheet) => sheet.kind === "keyValue");
  const routeSheets = sheets.filter((sheet) => sheet.kind === "routes");
  const primary = rosterSheets[0] || sheets.find((sheet) => sheet.kind !== "keyValue" && sheet.rows.length > 0) || sheets[0];
  if (!primary.rows.length) {
    throw new Error("The spreadsheet has headers but no student rows.");
  }

  const schoolsFromWorkbook = [];
  keyValueSheets.forEach((sheet) => {
    if (sheet.school?.name) schoolsFromWorkbook.push({ name: sheet.school.name, address: sheet.school.address || "" });
  });
  const routeList = routeSheets.flatMap((sheet) => (sheet.routes || []).map((route) => ({ ...route, sheetName: sheet.sheetName })));

  return {
    sheetName: primary.sheetName,
    extraSheets: sheetNames.filter((name) => name !== primary.sheetName),
    headers: primary.headers,
    rows: primary.rows,
    headerInferred: primary.headerInferred,
    sheets,
    rosterSheets,
    routeSheets,
    routeList,
    schoolsFromWorkbook,
    sheetNames,
  };
};

export const parseSpreadsheetArrayBuffer = (buffer) => {
  const workbook = XLSX.read(buffer, { type: "array" });
  const table = workbookToTable(workbook);
  const mapping = guessColumnMapping(table.headers);
  return {
    ...table,
    mapping,
    stats: summarizeParsedTable(table.rows, mapping),
  };
};

export const parseSpreadsheetFile = (file) =>
  new Promise((resolve, reject) => {
    const validation = validateSpreadsheetFile(file);
    if (!validation.ok) {
      reject(new Error(validation.error));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The spreadsheet could not be read in this browser."));
    reader.onload = () => {
      try {
        resolve(parseSpreadsheetArrayBuffer(reader.result));
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });

const mappedValue = (row, mapping, field) => {
  const index = mapping?.[field];
  if (index == null) return "";
  return cellString(row[index]);
};

export const passengerNameFromRow = (row, mapping) => {
  const full = mappedValue(row, mapping, "name");
  if (full) return full;
  return [mappedValue(row, mapping, "firstName"), mappedValue(row, mapping, "lastName")].filter(Boolean).join(" ");
};

export const buildPreviewFromMapping = (rows = [], mapping = {}, headers = []) => {
  const students = rows.map((row, index) => {
    const name = passengerNameFromRow(row, mapping);
    const school = mappedValue(row, mapping, "school");
    const address = mappedValue(row, mapping, "address");
    const parent = mappedValue(row, mapping, "parent");
    const phone = mappedValue(row, mapping, "phone");
    const email = mappedValue(row, mapping, "email");
    const grade = mappedValue(row, mapping, "grade");
    const missing = [];
    if (!name) missing.push("name");
    if (!school) missing.push("school");
    if (!address) missing.push("address");
    return {
      id: `row-${index + 1}`,
      rowNumber: index + 2,
      name: name || `Row ${index + 2}`,
      school: school || "Unassigned school",
      address,
      parent,
      phone,
      email,
      grade,
      missing,
      recognized: Boolean(name),
    };
  });

  const schoolMap = new Map();
  students.forEach((student) => {
    const key = student.school;
    if (!schoolMap.has(key)) schoolMap.set(key, { name: key, count: 0, missingAddress: 0 });
    const entry = schoolMap.get(key);
    entry.count += 1;
    if (!student.address) entry.missingAddress += 1;
  });

  return {
    students,
    schools: Array.from(schoolMap.values()),
    missingNameCount: students.filter((s) => s.missing.includes("name")).length,
    missingSchoolCount: students.filter((s) => s.missing.includes("school")).length,
    missingAddressCount: students.filter((s) => s.missing.includes("address")).length,
    recognizedCount: students.filter((s) => s.recognized).length,
    headers,
    mapping,
  };
};

export const summarizeParsedTable = (rows, mapping) => {
  const preview = buildPreviewFromMapping(rows, mapping);
  return {
    rowCount: rows.length,
    schoolCount: preview.schools.length,
    recognizedCount: preview.recognizedCount,
    missingAddressCount: preview.missingAddressCount,
    mappedFields: Object.entries(mapping)
      .filter(([, index]) => index != null)
      .map(([field]) => field),
  };
};

export const mappingIsUsable = (mapping) => {
  const fields = mapping || {};
  return fields.name != null || (fields.firstName != null && fields.lastName != null);
};
